import { inject, Injectable, signal } from '@angular/core';
import { UtilsService, CoreService } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { Invites, OrganizationMembers, SendInvitesResponse } from '../models/member.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationMembersService {

    // 1. Create Signals to hold the state
    private _membersData = signal<OrganizationMembers | null>(null);
    private _invitesData = signal<Invites[]>([]);
    private _membersDataOrgId = signal<number | null>(null);

    // 2. Expose read-only signals for components
    public membersData = this._membersData.asReadonly();
    public invitesData = this._invitesData.asReadonly();
    public membersDataOrgId = this._membersDataOrgId.asReadonly();

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);

    private identityBaseUrl = environment.identityUrl;

    // constructor() { }

    // 3. Modify fetch logic to update the signal
    async loadMembersIfNeeded(orgId: number, force = false): Promise<void> {
        // If we already have data and not forcing refresh, do nothing
        const currentMembersOrgId = this._membersDataOrgId();
        const hasMembersForOrg = this._membersData() && currentMembersOrgId === orgId;
        if (hasMembersForOrg && !force) {
            return;
        }

        const response = await this.fetchMembers(orgId); // Your existing HTTP call
        this.utils.logger.info('loadMembersIfNeeded() Fetched members response:', response);
        if (response) {
            this._membersData.set(response);
            this._membersDataOrgId.set(orgId);

            // Logic for invites (if needed immediately)
            if (response.pendingInvites?.length > 0) {
                 const invitesRes = await this.fetchInvites(orgId, 'Pending', 1, response.pendingInvites.length);
                 this._invitesData.set(Array.isArray(invitesRes) ? invitesRes : [invitesRes]);
            } else {
                 this._invitesData.set([]);
            }
        } else {
            this._membersData.set(null);
            this._membersDataOrgId.set(null);
            this._invitesData.set([]);
        }
    }

    public clearCache(): void {
        this._membersData.set(null);
        this._membersDataOrgId.set(null);
        this._invitesData.set([]);
    }

    async fetchMembers(
        orgId: number,
        q: string = '',
        page: number = 1,
        pageSize: number = 25
    ): Promise<OrganizationMembers | null> {

        this.utils.logger.info('fetchMembers() Fetching members for org:', orgId, 'page:', page);

        if (orgId == null) {
            this.utils.logger.warn('fetchMembers() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${orgId}/members?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`;
            this.utils.logger.info('fetchMembers() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            return data as OrganizationMembers;

        } catch (error) {
            this.utils.logger.error('fetchMembers() API error details:', error);
            return null;

        } finally {
            this.utils.logger.info('fetchMembers() Completed fetching members for org:', orgId);
        }
    }

    /**
     * Send organization invites
     * API: POST /Organization/{orgId}/invites
     */
    async sendInvites(
        orgId: number,
        invites: { email: string; role: string }[]
    ): Promise<SendInvitesResponse | null> {
        this.utils.logger.info('sendInvites() Sending invites for org:', orgId);

        if (orgId == null || !invites?.length) {
            this.utils.logger.warn('sendInvites() Missing orgId or invites');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${orgId}/invites`;
            this.utils.logger.info('sendInvites() API URL:', url);

            const payload = { invites };
            const data = await firstValueFrom(this.core.post(url, payload));

            return data as SendInvitesResponse;

        } catch (error) {
            this.utils.logger.error('sendInvites() API error details:', error);
            return null;

        } finally {
            this.utils.logger.info('sendInvites() Completed sending invites for org:', orgId);
        }
    }

    /**
     * Fetch organization invites
     * API: POST /Organization/{orgId}/invites
     */
    async fetchInvites(
        orgId: number,
        q: string = '',
        page: number = 1,
        pageSize: number = 25
    ): Promise<Invites | null> {

        this.utils.logger.info('fetchInvites() Fetching invites for org:', orgId, 'page:', page);

        if (orgId == null) {
            this.utils.logger.warn('fetchInvites() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${orgId}/invites?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`;
            this.utils.logger.info('fetchInvites() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            return data as Invites;

        } catch (error) {
            this.utils.logger.error('fetchInvites() API error details:', error);
            return null;

        } finally {
            this.utils.logger.info('fetchInvites() Completed fetching invites for org:', orgId);
        }
    }

    /**
     * Resend organization invite
     * API: POST /Organization/{organizationId}/invites/{inviteId}/resend
     */
    async resendInvite(
        organizationId: number,
        inviteId: string,
        email: string
    ): Promise<Invites | null> {
        this.utils.logger.info('resendInvite() Resending invite:', inviteId);

        if (!organizationId || !inviteId || !email) {
            this.utils.logger.warn('resendInvite() Missing organizationId, inviteId, or email');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${organizationId}/invites/${inviteId}/resend`;
            this.utils.logger.info('resendInvite() API URL:', url);

            const payload = { email };
            const data = await firstValueFrom(this.core.post(url, payload));

            return data as Invites;

        } catch (error) {
            this.utils.logger.error('resendInvite() API error details:', error);
            return null;

        } finally {
            this.utils.logger.info('resendInvite() Completed resend for invite:', inviteId);
        }
    }

    /**
     * Revoke organization invite
     * API: POST /Organization/{organizationId}/invites/{inviteId}/revoke
     */
    async revokeInvite(
        organizationId: number,
        inviteId: string
    ): Promise<Invites | null> {
        this.utils.logger.info('revokeInvite() Revoking invite:', inviteId);

        if (!organizationId || !inviteId) {
            this.utils.logger.warn('revokeInvite() Missing organizationId or inviteId');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Organization/${organizationId}/invites/${inviteId}/revoke`;
            this.utils.logger.info('revokeInvite() API URL:', url);

            const data = await firstValueFrom(this.core.post(url, {}));

            return data as Invites;

        } catch (error) {
            this.utils.logger.error('revokeInvite() API error details:', error);
            return null;

        } finally {
            this.utils.logger.info('revokeInvite() Completed revoke for invite:', inviteId);
        }
    }

    /**
   * Updates the role of a member in an organization.
   * @param orgId The organization ID
   * @param membershipId The membership/user ID
   * @param newRole The new role to assign
   * @returns A promise that resolves on success.
   */
    async updateRole(orgId: number | string, membershipId: number | string, newRole: string, ): Promise<any> {
        const url = `${this.identityBaseUrl}/Organization/${orgId}/members/${membershipId}/role`;
        const payload = { role: newRole };

        try {
            return await firstValueFrom(this.core.bulkPut(url, payload));
        } catch (error) {
            // Optional: handle error in your app's manner
            this.utils.logger.error('Failed to update role', error);
            return null;
        }
        finally {
            this.utils.logger.info('Completed role update for member:', membershipId);
        }
    }

    /**
   * Removes a member from an organization.
   * @param orgId The organization ID
   * @param membershipId The membership/user ID
   * @returns A promise that resolves when the member is deleted.
   */
    async removeMember(
        orgId: number,
        membershipId: string
    ): Promise<any> {
        const url = `${this.identityBaseUrl}/Organization/${orgId}/members/${membershipId}`;
        try {
            return await firstValueFrom(this.core.delete(url));
        } catch (error) {
            this.utils.logger.error('Failed to delete member', error);
            throw error;
        }
    }
}
